/**
 * useWorkspaces Hook
 *
 * Fetches all workspaces the current user has access to.
 * Used for workspace switching in navigation.
 *
 * Usage:
 * ```tsx
 * const { workspaces, isLoading } = useWorkspaces();
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import type { WorkspaceRole } from '@loader/shared-types';

import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';
import { sortWorkspacesByName } from '@/utils/workspaceSorting';

/**
 * Workspace option for selection
 * Represents a workspace with the user's role
 */
export interface WorkspaceOption {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  role: WorkspaceRole;
}

/**
 * Cache duration for workspaces list (5 minutes)
 * Workspaces rarely change during a session
 */
const WORKSPACES_CACHE_TIME = 5 * 60 * 1000;

/**
 * Return type for useWorkspaces hook
 */
export interface UseWorkspacesReturn {
  /** List of workspaces */
  workspaces: WorkspaceOption[];
  /** Whether workspaces are loading */
  isLoading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Refetch workspaces */
  refetch: () => void;
}

/**
 * Hook to fetch all workspaces for the current user
 *
 * Uses TanStack Query with a 5-minute cache for optimal performance.
 */
export function useWorkspaces(): UseWorkspacesReturn {
  const rpc = useRpc();

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.workspaces.all,
    queryFn: async (): Promise<WorkspaceOption[]> => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces.all.$get();
      if (!res.ok) return [];
      const response = await res.json();
      const workspaces = response.workspaces ?? [];
      return sortWorkspacesByName(workspaces);
    },
    staleTime: WORKSPACES_CACHE_TIME,
  });

  return {
    workspaces: data ?? [],
    isLoading,
    error,
    refetch,
  };
}
